import { useState, useRef, useEffect } from 'react';
import './App.css';

interface HistoryRecord {
  inputText: string;
  expectedText: string;
  regexPattern: string;
  timestamp: string;
}

interface ValidationResult {
  text: string;
  result: string;
  isValid: boolean;
  expectedText: string;
}

interface TextGroup {
  input: string;
  expected: string;
}

function App() {
  const [textGroups, setTextGroups] = useState<TextGroup[]>([{ input: '', expected: '' }]);
  const [regexPattern, setRegexPattern] = useState('');
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [testResult, setTestResult] = useState<{success: boolean; message: string} | null>(null);
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [historyPanelWidth, setHistoryPanelWidth] = useState(600);
  const resizerRef = useRef<HTMLDivElement>(null);
  const historyPanelRef = useRef<HTMLDivElement>(null);

  // 用于文件输入的ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [regexOptimizerInput, setRegexOptimizerInput] = useState('');
  const [regexOptimizerResult, setRegexOptimizerResult] = useState('');

  useEffect(() => {
    const resizer = resizerRef.current;
    const historyPanel = historyPanelRef.current;
    if (!resizer || !historyPanel) return;

    let startX: number;
    let startWidth: number;

    const onMouseDown = (e: MouseEvent) => {
      startX = e.clientX;
      startWidth = historyPanel.offsetWidth;
      resizer.classList.add('dragging');
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!historyPanel) return;
      const delta = startX - e.clientX;
      const newWidth = Math.min(Math.max(400, startWidth + delta), 1000);
      setHistoryPanelWidth(newWidth);
    };

    const onMouseUp = () => {
      resizer.classList.remove('dragging');
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    resizer.addEventListener('mousedown', onMouseDown);

    return () => {
      resizer.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // 添加新的输入组
  const addTextGroup = () => {
    setTextGroups(prev => [...prev, { input: '', expected: '' }]);
  };

  // 删除输入组
  const removeTextGroup = (index: number) => {
    if (textGroups.length > 1) {
      setTextGroups(prev => prev.filter((_, i) => i !== index));
    }
  };

  // 更新输入组
  const updateTextGroup = (index: number, field: 'input' | 'expected', value: string) => {
    setTextGroups(prev => prev.map((group, i) => 
      i === index ? { ...group, [field]: value } : group
    ));
  };

  // 比较两个正则表达式的通用性
  const compareRegexGenerality = (regex1: string, regex2: string, testCases: string[]): number => {
    try {
      const r1 = new RegExp(regex1);
      const r2 = new RegExp(regex2);
      
      // 计算每个正则表达式匹配的测试用例数量
      let matches1 = 0;
      let matches2 = 0;
      
      // 对每个测试用例，检查是否能正确匹配任一输入组的期望文本
      for (const testCase of testCases) {
        for (const group of textGroups) {
          const result1 = testCase.replace(r1, group.expected);
          const result2 = testCase.replace(r2, group.expected);
          
          if (result1 === group.expected) matches1++;
          if (result2 === group.expected) matches2++;
        }
      }
      
      // 如果匹配数量不同，返回匹配数量更多的
      if (matches1 !== matches2) {
        return matches1 > matches2 ? 1 : -1;
      }
      
      // 如果匹配数量相同，返回更短的正则表达式
      return regex1.length <= regex2.length ? 1 : -1;
    } catch (error) {
      return 0; // 如果出现错误，认为它们相等
    }
  };

  // 生成测试用例
  const generateTestCases = (groups: TextGroup[]): string[] => {
    const testCases = new Set<string>();
    
    groups.forEach(group => {
      const inputLines = group.input.split('\n').filter(line => line.trim());
      inputLines.forEach(line => {
        testCases.add(line);
        // 添加数字变体
        testCases.add(line.replace(/\d+/g, '123'));
        // 添加去除数字的变体
        testCases.add(line.replace(/\d+/g, ''));
        // 添加替换月份的变体
        for (let i = 1; i <= 12; i++) {
          testCases.add(line.replace(/\d+月/, `${i}月`));
        }
      });
    });
    
    return Array.from(testCases);
  };

  // 提取正则表达式的共同模式
  const extractCommonPattern = (patterns: string[]): string => {
    if (patterns.length === 0) return '';
    if (patterns.length === 1) return patterns[0];

    try {
      // 移除 ^ 和 $ 符号以便于比较
      const cleanPatterns = patterns.map(p => p.replace(/^\^|\$$/g, ''));
      
      // 找到共同的前缀和后缀
      let prefix = '';
      let suffix = '';
      let i = 0;
      
      // 查找共同前缀
      while (true) {
        const char = cleanPatterns[0][i];
        if (!char || !cleanPatterns.every(p => p[i] === char)) break;
        prefix += char;
        i++;
      }
      
      // 查找共同后缀
      if (prefix !== cleanPatterns[0]) {
        let j = 1;
        while (true) {
          const positions = cleanPatterns.map(p => p.length - j);
          if (positions.some(pos => pos < prefix.length)) break;
          const chars = positions.map(pos => cleanPatterns[0][pos]);
          if (!chars.every(c => c === chars[0])) break;
          suffix = chars[0] + suffix;
          j++;
        }
      }
      
      // 处理中间的可变部分
      const middle = cleanPatterns.map(p => {
        const withoutPrefixSuffix = p.slice(prefix.length, p.length - suffix.length);
        return withoutPrefixSuffix || '([\\s\\S]*?)';
      });
      
      // 如果所有中间部分都包含捕获组，使用单个捕获组
      const allHaveCapture = middle.every(m => m.includes('([\\s\\S]*?)'));
      const middlePart = allHaveCapture ? '([\\s\\S]*?)' : '([\\s\\S]*?)';
      
      return '^' + prefix + middlePart + suffix + '$';
    } catch (error) {
      console.error('提取共同模式时出错:', error);
      return patterns[0];
    }
  };

  // 合并并去重多行文本
  const mergeAndDeduplicate = (arr: string[]): string => {
    return Array.from(new Set(arr.flatMap(item => item.split('\n').map(line => line.trim()).filter(Boolean)))).join('\n');
  };

  // 判断一个正则是否能覆盖所有输入并正确替换为期望文本
  const canRegexCoverAll = (regex: string, inputArr: string[]): boolean => {
    try {
      const reg = new RegExp(regex);
      return inputArr.every(input => reg.test(input));
    } catch {
      return false;
    }
  };

  // 按捕获组整体分块正则表达式（支持多种捕获组写法）
  const splitRegexBlocks = (pattern: string): string[] => {
    const blocks: string[] = [];
    // 支持 ([\s\S]*?)、([\\s\\S]*?)、([sS]*?)
    const groupPattern = /\(\[\\s\\S\]\*\?\)|\(\[\\sS\]\*\?\)|\(\[sS\]\*\?\)|\(\[\s\S\]\*\?\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = groupPattern.exec(pattern)) !== null) {
      if (match.index > lastIndex) {
        blocks.push(pattern.slice(lastIndex, match.index));
      }
      blocks.push(match[0]);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < pattern.length) {
      blocks.push(pattern.slice(lastIndex));
    }
    return blocks.filter(Boolean);
  };

  // 获取所有字符串的最长公共前缀
  const getCommonPrefix = (arr: string[]): string => {
    if (arr.length === 0) return '';
    let prefix = arr[0];
    for (let i = 1; i < arr.length; i++) {
      let j = 0;
      while (j < prefix.length && j < arr[i].length && prefix[j] === arr[i][j]) j++;
      prefix = prefix.slice(0, j);
      if (!prefix) break;
    }
    return prefix;
  };
  // 获取所有字符串的最长公共后缀
  const getCommonSuffix = (arr: string[]): string => {
    if (arr.length === 0) return '';
    let suffix = arr[0];
    for (let i = 1; i < arr.length; i++) {
      let j = 0;
      while (
        j < suffix.length &&
        j < arr[i].length &&
        suffix[suffix.length - 1 - j] === arr[i][arr[i].length - 1 - j]
      ) j++;
      suffix = suffix.slice(suffix.length - j);
      if (!suffix) break;
    }
    return suffix;
  };

  // 捕获组标准化为([\s\S]*?)
  const normalizeCaptureGroup = (str: string): string => {
    return str.replace(/\(\[\\s\\S\]\*\?\)|\(\[\\sS\]\*\?\)|\(\[sS\]\*\?\)|\(\[\s\S\]\*\?\)/g, '([\\s\\S]*?)');
  };

  // 智能分块：捕获组整体+普通文本自动按最长公共前缀/后缀切分（捕获组标准化）
  const smartSplitRegexBlocks = (patterns: string[]): string[][] => {
    // 先按捕获组整体分块
    const groupPattern = /\(\[\\s\\S\]\*\?\)|\(\[\\sS\]\*\?\)|\(\[sS\]\*\?\)|\(\[\s\S\]\*\?\)/g;
    const allBlocks: string[][] = patterns.map(pattern => {
      const normPattern = normalizeCaptureGroup(pattern);
      const blocks: string[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = groupPattern.exec(normPattern)) !== null) {
        if (match.index > lastIndex) {
          blocks.push(normPattern.slice(lastIndex, match.index));
        }
        blocks.push('([\\s\\S]*?)');
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < normPattern.length) {
        blocks.push(normPattern.slice(lastIndex));
      }
      return blocks.filter(Boolean);
    });
    // 对每一组普通文本块，按最长公共前缀/后缀切分
    const maxLen = Math.max(...allBlocks.map(b => b.length));
    for (let i = 0; i < maxLen; i++) {
      // 收集所有正则的第i块
      const ithBlocks = allBlocks.map(b => b[i] || '');
      // 跳过捕获组
      if (ithBlocks.every(b => b === '([\\s\\S]*?)')) continue;
      // 只处理普通文本
      const prefix = getCommonPrefix(ithBlocks);
      const suffix = getCommonSuffix(ithBlocks);
      // 如果有公共前缀或后缀且不是整块
      if ((prefix && prefix.length < ithBlocks[0].length) || (suffix && suffix.length < ithBlocks[0].length)) {
        for (let j = 0; j < allBlocks.length; j++) {
          const block = allBlocks[j][i] || '';
          const mid = block.slice(prefix.length, block.length - suffix.length);
          const newBlocks = [];
          if (prefix) newBlocks.push(prefix);
          if (mid) newBlocks.push(mid);
          if (suffix) newBlocks.push(suffix);
          // 替换原块
          allBlocks[j].splice(i, 1, ...newBlocks);
        }
      }
    }
    return allBlocks;
  };

  // 合并多个正则表达式，差异部分用([\s\S]*?)替换（智能分块LCS）
  const mergeRegexPatterns = (patterns: string[]): string => {
    if (patterns.length === 1) return patterns[0];
    // 去掉^和$，只对主体做分块LCS
    const bodies = patterns.map(p => p.replace(/^\^|\$$/g, ''));
    let allBlocks = smartSplitRegexBlocks(bodies);
    let baseBlocks = allBlocks[0];
    for (let i = 1; i < allBlocks.length; i++) {
      const bBlocks = allBlocks[i];
      // LCS on blocks
      const m = baseBlocks.length, n = bBlocks.length;
      const dp: number[][] = Array(m + 1).fill(0).map(() => Array(n + 1).fill(0));
      for (let x = 1; x <= m; x++) {
        for (let y = 1; y <= n; y++) {
          if (baseBlocks[x - 1] === bBlocks[y - 1]) {
            dp[x][y] = dp[x - 1][y - 1] + 1;
          } else {
            dp[x][y] = Math.max(dp[x - 1][y], dp[x][y - 1]);
          }
        }
      }
      // 回溯LCS索引
      let lcsIdxA: number[] = [], lcsIdxB: number[] = [];
      let x = m, y = n;
      while (x > 0 && y > 0) {
        if (baseBlocks[x - 1] === bBlocks[y - 1]) {
          lcsIdxA.unshift(x - 1);
          lcsIdxB.unshift(y - 1);
          x--;
          y--;
        } else if (dp[x - 1][y] > dp[x][y - 1]) {
          x--;
        } else {
          y--;
        }
      }
      // 合并：LCS块之间插入([\s\S]*?)
      let merged: string[] = [];
      let lastA = 0, lastB = 0;
      for (let k = 0; k < lcsIdxA.length; k++) {
        const ai = lcsIdxA[k];
        const bi = lcsIdxB[k];
        if (ai > lastA || bi > lastB) {
          merged.push('([\s\S]*?)');
        }
        merged.push(baseBlocks[ai]);
        lastA = ai + 1;
        lastB = bi + 1;
      }
      if (lastA < baseBlocks.length || lastB < bBlocks.length) {
        merged.push('([\s\S]*?)');
      }
      baseBlocks = merged;
    }
    let result = '^' + baseBlocks.join('') + '$';
    // 全局标准化捕获组写法
    result = result.replace(/\(\[sS\]\*\?\)|\(\[\\s\\S\]\*\?\)|\(\[\\sS\]\*\?\)|\(\[\s\S\]\*\?\)/g, '([\\s\\S]*?)');
    return result;
  };

  // 优化历史记录中的正则表达式（全局最通用合并）
  const optimizeHistoryRegex = (records: HistoryRecord[]): HistoryRecord[] => {
    try {
      if (records.length <= 1) return records;
      // 收集所有正则、所有输入、所有期望
      const allPatterns = records.map(r => r.regexPattern);
      const allInputsArr = records.map(r => r.inputText.split('\n---\n').flatMap(s => s.split('\n').map(line => line.trim()).filter(Boolean)));
      const allExpectedArr = records.map(r => r.expectedText.split('\n---\n').flatMap(s => s.split('\n').map(line => line.trim()).filter(Boolean)));
      const allInputs = mergeAndDeduplicate(allInputsArr.flat());
      const allExpected = mergeAndDeduplicate(allExpectedArr.flat());
      const allInputsList = allInputs.split('\n');
      const allExpectedList = allExpected.split('\n');

      // 用LCS合并正则表达式
      const bestPattern = mergeRegexPatterns(allPatterns);
      // 用 bestPattern 验证所有输入-期望组合
      if (canRegexCoverAll(bestPattern, allInputsList)) {
        return [{
          inputText: allInputs,
          expectedText: allExpected,
          regexPattern: bestPattern,
          timestamp: new Date().toLocaleString('zh-CN')
        }];
      }
      // 否则分别保留
      return records.map(r => ({
        inputText: mergeAndDeduplicate(r.inputText.split('\n---\n').flatMap(s => s.split('\n').map(line => line.trim()).filter(Boolean))),
        expectedText: mergeAndDeduplicate(r.expectedText.split('\n---\n').flatMap(s => s.split('\n').map(line => line.trim()).filter(Boolean))),
        regexPattern: r.regexPattern,
        timestamp: r.timestamp
      }));
    } catch (error) {
      console.error('优化正则表达式时出错:', error);
      return records;
    }
  };

  // 更新历史记录（不做自动优化，仅保存新记录）
  const updateHistory = (newRecord: HistoryRecord, testCases: string[]) => {
    setHistory(prevHistory => {
      return [...prevHistory, newRecord];
    });
  };

  // 生成更通用的正则表达式（用([\s\S]*?)替换差异部分）
  const generateRegexFromInputAndExpected = (input: string, expected: string): string => {
    // LCS算法
    const m = input.length;
    const n = expected.length;
    const dp: number[][] = Array(m + 1).fill(0).map(() => Array(n + 1).fill(0));
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (input[i - 1] === expected[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }
    // 回溯LCS
    let lcs = '';
    let i = m, j = n;
    const lcsPos: [number, number][] = [];
    while (i > 0 && j > 0) {
      if (input[i - 1] === expected[j - 1]) {
        lcs = input[i - 1] + lcs;
        lcsPos.unshift([i - 1, j - 1]);
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }
    // 构建正则
    let regex = '^';
    let lastInputIdx = 0;
    for (let k = 0; k < lcsPos.length; k++) {
      const [inputIdx, expectedIdx] = lcsPos[k];
      if (inputIdx > lastInputIdx) {
        regex += '([\\s\\S]*?)';
      }
      regex += escapeRegExp(input[inputIdx]);
      lastInputIdx = inputIdx + 1;
    }
    if (lastInputIdx < input.length) {
      regex += '([\\s\\S]*?)';
    }
    regex += '$';
    return regex;
  };

  // 替换 generateRegex 逻辑，支持多组输入
  const generateRegex = () => {
    try {
      if (textGroups.some(group => !group.input || !group.expected)) {
        setRegexPattern('请确保所有输入组都已填写完整');
        return;
      }
      // 只处理单组时的智能生成
      if (textGroups.length === 1) {
        const group = textGroups[0];
        const inputLines = group.input.split('\n').filter(line => line.trim());
        const expected = group.expected;
        if (inputLines.length === 1) {
          const regex = generateRegexFromInputAndExpected(inputLines[0], expected);
          setRegexPattern(regex);
          setTestResult(null);
          setValidationResults([]);
          verifyRegex(regex);
          return;
        }
      }
      // 多组或多行时，仍用原有LCS逻辑
      // 获取所有输入行
      const allInputLines = textGroups.flatMap(group => 
        group.input.split('\n').filter(line => line.trim())
      );
      if (allInputLines.length === 0) {
        setRegexPattern('请至少输入一行有效文本');
        return;
      }
      // 找到所有行的共同模式
      const findLCS = (str1: string, str2: string) => {
        const m = str1.length;
        const n = str2.length;
        const dp: number[][] = Array(m + 1).fill(0).map(() => Array(n + 1).fill(0));
        for (let i = 1; i <= m; i++) {
          for (let j = 1; j <= n; j++) {
            if (str1[i - 1] === str2[j - 1]) {
              dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
              dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
          }
        }
        let lcs = '';
        let i = m, j = n;
        while (i > 0 && j > 0) {
          if (str1[i - 1] === str2[j - 1]) {
            lcs = str1[i - 1] + lcs;
            i--;
            j--;
          } else if (dp[i - 1][j] > dp[i][j - 1]) {
            i--;
          } else {
            j--;
          }
        }
        return lcs;
      };
      let commonPattern = allInputLines[0];
      for (let i = 1; i < allInputLines.length; i++) {
        commonPattern = findLCS(commonPattern, allInputLines[i]);
      }
      if (!commonPattern) {
        setRegexPattern('^.*$');
        return;
      }
      let regexParts: string[] = [];
      let currentPos = 0;
      let patternPos = 0;
      regexParts.push('^');
      const firstLine = allInputLines[0];
      for (let i = 0; i < firstLine.length; i++) {
        if (patternPos < commonPattern.length && firstLine[i] === commonPattern[patternPos]) {
          if (i > currentPos) {
            regexParts.push('([\\s\\S]*?)');
          }
          regexParts.push(escapeRegExp(firstLine[i]));
          currentPos = i + 1;
          patternPos++;
        }
      }
      if (currentPos < firstLine.length) {
        regexParts.push('([\\s\\S]*?)');
      }
      regexParts.push('$');
      const regex = regexParts.join('');
      setRegexPattern(regex);
      setTestResult(null);
      setValidationResults([]);
      verifyRegex(regex);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      setRegexPattern('生成正则表达式时出错：' + errorMessage);
    }
  };

  const verifyRegex = (patternToVerify: string = regexPattern) => {
    try {
      if (!patternToVerify) {
        setTestResult({
          success: false,
          message: '请先生成或输入正则表达式'
        });
        return;
      }

      try {
        const testRegex = new RegExp(patternToVerify);
        const results: ValidationResult[] = [];
        let allValid = true;

        // 验证每个输入组的每一行
        for (const group of textGroups) {
          const inputLines = group.input.split('\n').filter(line => line.trim());
          for (const line of inputLines) {
            const result = line.replace(testRegex, group.expected);
            const isValid = result === group.expected;
            results.push({
              text: line,
              result,
              isValid,
              expectedText: group.expected
            });
            if (!isValid) allValid = false;
          }
        }

        setValidationResults(results);
        setTestResult({
          success: allValid,
          message: allValid ? '所有文本验证成功' : '部分文本验证失败'
        });

        // 如果验证全部成功，更新历史记录
        if (allValid) {
          const newRecord: HistoryRecord = {
            inputText: textGroups.map(g => g.input).join('\n---\n'),
            expectedText: textGroups.map(g => g.expected).join('\n---\n'),
            regexPattern: patternToVerify,
            timestamp: new Date().toLocaleString('zh-CN')
          };

          // 生成测试用例并更新历史记录
          const testCases = generateTestCases(textGroups);
          updateHistory(newRecord, testCases);
        }
      } catch (regexError) {
        setTestResult({
          success: false,
          message: '正则表达式格式错误'
        });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      setTestResult({
        success: false,
        message: '验证出错：' + errorMessage
      });
    }
  };

  // 转义正则表达式特殊字符
  const escapeRegExp = (string: string): string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // 保存到CSV文件
  const saveToCSV = () => {
    try {
      if (history.length === 0) {
        setTestResult({
          success: false,
          message: '没有可保存的历史记录'
        });
        return;
      }

      // 准备CSV内容
      const headers = '"输入文本","期望文本","正则表达式","时间"\n';
      const rows = history.map(record => {
        return [
          record.inputText.replace(/"/g, '""'),
          record.expectedText.replace(/"/g, '""'),
          record.regexPattern.replace(/"/g, '""'),
          record.timestamp.replace(/"/g, '""')
        ].map(field => `"${field}"`).join(',');
      }).join('\n');

      const csvContent = headers + rows;
      
      // 创建Blob对象并下载
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', 'regex_patterns.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setTestResult({
        success: true,
        message: '成功保存到CSV文件'
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      setTestResult({
        success: false,
        message: '保存文件时出错：' + errorMessage
      });
    }
  };

  // 导入CSV文件
  const importFromCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        // 使用更可靠的CSV解析方法
        const records = parseCSV(text);
        
        if (records.length === 0) {
          throw new Error('没有找到有效的记录');
        }

        // 直接优化导入的记录
        const optimizedRecords = optimizeHistoryRegex(records);
        
        // 更新历史记录
        setHistory(optimizedRecords);

        setTestResult({
          success: true,
          message: `成功导入并优化 ${optimizedRecords.length} 条历史记录`
        });

        // 清除文件输入，允许重复导入相同文件
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setTestResult({
          success: false,
          message: '导入CSV文件失败：' + (error instanceof Error ? error.message : '未知错误')
        });
      }
    };
    reader.readAsText(file);
  };

  // 解析CSV文件
  const parseCSV = (csvText: string): HistoryRecord[] => {
    const records: HistoryRecord[] = [];
    
    // 移除BOM标记（如果存在）
    if (csvText.charCodeAt(0) === 0xFEFF) {
      csvText = csvText.slice(1);
    }

    // 将CSV文本分割成记录
    let currentChar = '';
    let currentField = '';
    let fields: string[] = [];
    let insideQuotes = false;
    let i = 0;

    // 跳过标题行
    while (i < csvText.length && csvText[i] !== '\n') i++;
    i++; // 跳过换行符

    // 解析数据行
    while (i < csvText.length) {
      currentChar = csvText[i];

      if (currentChar === '"') {
        if (insideQuotes && csvText[i + 1] === '"') {
          // 处理转义的引号
          currentField += '"';
          i++;
        } else {
          // 切换引号状态
          insideQuotes = !insideQuotes;
        }
      } else if (currentChar === ',' && !insideQuotes) {
        // 字段结束
        fields.push(currentField);
        currentField = '';
      } else if (currentChar === '\n' && !insideQuotes) {
        // 记录结束
        fields.push(currentField);
        if (fields.length >= 4) {
          records.push({
            inputText: fields[0],
            expectedText: fields[1],
            regexPattern: fields[2],
            timestamp: fields[3]
          });
        }
        fields = [];
        currentField = '';
      } else {
        // 普通字符
        currentField += currentChar;
      }
      i++;
    }

    // 处理最后一个记录
    if (currentField) {
      fields.push(currentField);
      if (fields.length >= 4) {
        records.push({
          inputText: fields[0],
          expectedText: fields[1],
          regexPattern: fields[2],
          timestamp: fields[3]
        });
      }
    }

    // 清理记录中的引号和多余空格
    return records.map(record => ({
      ...record,
      inputText: cleanField(record.inputText),
      expectedText: cleanField(record.expectedText),
      regexPattern: cleanField(record.regexPattern),
      timestamp: cleanField(record.timestamp)
    }));
  };

  // 清理CSV字段
  const cleanField = (field: string): string => {
    // 移除首尾引号
    if (field.startsWith('"') && field.endsWith('"')) {
      field = field.slice(1, -1);
    }
    // 替换双引号
    field = field.replace(/""/g, '"');
    // 修剪空格但保留内部换行
    return field.replace(/^\s+|\s+$/g, '');
  };

  // 优化历史记录按钮点击处理
  const handleOptimizeHistory = () => {
    if (history.length === 0) {
      setTestResult({
        success: false,
        message: '没有可优化的历史记录'
      });
      return;
    }

    const optimizedHistory = optimizeHistoryRegex(history);
    setHistory(optimizedHistory);
    setTestResult({
      success: true,
      message: `历史记录优化完成，现有 ${optimizedHistory.length} 条记录`
    });
  };

  // 正则优化器功能
  const handleOptimizeRegexInput = () => {
    const patterns = regexOptimizerInput
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);
    if (patterns.length < 2) {
      setRegexOptimizerResult('请至少输入两条正则表达式');
      return;
    }
    let result = mergeRegexPatterns(patterns);
    result = unescapeRegex(result);
    setRegexOptimizerResult(result);
  };

  // 去除正则中的多余转义
  const unescapeRegex = (pattern: string): string => {
    // 只保留必要的转义（如括号、点、加号等），去除多余的反斜杠
    // 这里主要针对 mergeRegexPatterns 生成的 ([\\s\\S]*?) 变成 ([\s\S]*?)
    return pattern.replace(/\\([\\sS])/g, '\\$1').replace(/\\([(){}.?+*|\[\]^$])/g, '\\$1');
  };

  return (
    <div className="App">
      <div className="main-content">
        <header className="App-header">
          <h1>正则表达式生成器</h1>
          <div className="input-container">
            {textGroups.map((group, index) => (
              <div key={index} className="text-group">
                <div className="text-group-header">
                  <h3>输入组 {index + 1}</h3>
                  {textGroups.length > 1 && (
                    <button 
                      onClick={() => removeTextGroup(index)}
                      className="remove-button"
                    >
                      删除
                    </button>
                  )}
                </div>
                <div className="input-group">
                  <label>输入文本（每行一条）：</label>
                  <textarea
                    value={group.input}
                    onChange={(e) => updateTextGroup(index, 'input', e.target.value)}
                    placeholder="请输入原始文本，每行一条"
                    rows={5}
                  />
                </div>
                <div className="input-group">
                  <label>期望文本：</label>
                  <input
                    type="text"
                    value={group.expected}
                    onChange={(e) => updateTextGroup(index, 'expected', e.target.value)}
                    placeholder="请输入期望文本"
                  />
                </div>
              </div>
            ))}
            <button onClick={addTextGroup} className="add-button">
              添加输入组
            </button>
            <div className="input-group">
              <label>正则表达式：</label>
              <input
                type="text"
                value={regexPattern}
                onChange={(e) => {
                  setRegexPattern(e.target.value);
                  setValidationResults([]);
                }}
                placeholder="输入或生成的正则表达式"
              />
            </div>
            <div className="button-group">
              <button onClick={generateRegex}>生成正则表达式</button>
              <button onClick={() => verifyRegex()}>验证正则表达式</button>
            </div>
          </div>
          <div className="result-container">
            <div className="validation-results">
              <h3>验证结果：</h3>
              {validationResults.map((result, index) => (
                <div key={index} className={`validation-item ${result.isValid ? 'valid' : 'invalid'}`}>
                  <div className="validation-text">
                    <span>原文本：</span>{result.text}
                  </div>
                  <div className="validation-expected">
                    <span>期望文本：</span>{result.expectedText}
                  </div>
                  <div className="validation-result">
                    <span>实际结果：</span>{result.result}
                  </div>
                </div>
              ))}
            </div>
            {testResult && (
              <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                {testResult.message}
              </div>
            )}
          </div>
        </header>
      </div>
      <div className="regex-optimizer-section" style={{ maxWidth: 800, margin: '40px auto 0', background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: 24 }}>
        <h2 style={{ marginTop: 0 }}>正则优化器</h2>
        <div style={{ marginBottom: 12 }}>
          <textarea
            style={{ width: '100%', minHeight: 80, fontFamily: 'monospace', fontSize: 15, padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
            placeholder="每行输入一个正则表达式"
            value={regexOptimizerInput}
            onChange={e => setRegexOptimizerInput(e.target.value)}
          />
        </div>
        <button onClick={handleOptimizeRegexInput} style={{ padding: '8px 24px', fontSize: 15 }}>优化正则表达式</button>
        {regexOptimizerResult && (
          <div style={{ marginTop: 18, background: '#f8f9fa', borderRadius: 4, padding: 12, fontFamily: 'monospace', wordBreak: 'break-all', border: '1px solid #eee' }}>
            <strong>优化结果：</strong>
            <div>{regexOptimizerResult}</div>
          </div>
        )}
      </div>
      <div 
        className="history-panel" 
        ref={historyPanelRef}
        style={{ width: `${historyPanelWidth}px` }}
      >
        <div className="history-panel-resizer" ref={resizerRef} />
        <div className="history-panel-header">
          <h2>历史记录</h2>
          <div className="history-panel-actions">
            <button onClick={saveToCSV} className="save-button">
              导出CSV
            </button>
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={importFromCSV}
            />
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="import-button"
            >
              导入CSV
            </button>
            <button 
              onClick={handleOptimizeHistory}
              className="optimize-button"
            >
              优化记录
            </button>
          </div>
        </div>
        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th className="timestamp-cell">时间</th>
                <th className="text-cell">输入文本</th>
                <th className="text-cell">期望文本</th>
                <th className="regex-cell">正则表达式</th>
              </tr>
            </thead>
            <tbody>
              {history.map((record, index) => (
                <tr key={index}>
                  <td className="timestamp-cell">{record.timestamp}</td>
                  <td className="text-cell">{record.inputText}</td>
                  <td className="text-cell">{record.expectedText}</td>
                  <td className="regex-cell">{record.regexPattern}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default App;
